/**
 * Field Catalog Builder (Mercell Admin Only)
 * Create and manage fields in the master catalog
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { catalogApi } from '../services/api';
import { isPlatformAdmin } from '../utils/adminAccess';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { NimbusTable } from '../components/NimbusTable';
import { PageHeader } from '../components/PageHeader';

type FieldType = 'text' | 'richtext' | 'dropdown' | 'searchable-dropdown' | 'multiselect' | 'person' | 'asset' | 'date' | 'number' | 'readonly' | 'calculated' | 'procedure';

interface CatalogField {
  catalogId: string;
  fieldId: string;
  fieldName: string;
  fieldType: FieldType;
  category: string;
  isRequired: boolean;
  isCalculated: boolean;
  isSystemField?: boolean;
  description?: string;
  placeholder?: string;
  helpText?: string;
  options?: string[];
  validationRules?: Record<string, any>;
}

export const FieldCatalogBuilder: React.FC = () => {
  const userEmail = localStorage.getItem('userEmail') || '';
  const queryClient = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  const [isCreating, setIsCreating] = useState(false);
  const [editingField, setEditingField] = useState<CatalogField | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('risk');
  const [fieldSearch, setFieldSearch] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    fieldId: '',
    fieldName: '',
    fieldType: 'text' as FieldType,
    category: 'risk',
    isRequired: false,
    isCalculated: false,
    isSystemField: false,
    description: '',
    placeholder: '',
    helpText: '',
    options: [] as string[],
    validationRules: {}
  });

  const [optionsText, setOptionsText] = useState(''); // For dropdown options (one per line)

  // Fetch catalog fields
  const { data: catalogFields = [], isLoading } = useQuery<CatalogField[]>({
    queryKey: ['catalogFields'],
    queryFn: catalogApi.getAll,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: catalogApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalogFields'] });
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
      resetForm();
      setIsCreating(false);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ catalogId, data }: { catalogId: string; data: any }) =>
      catalogApi.update(catalogId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['catalogFields'] });
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
      // Keep the form open with updated data
      if (data.field) {
        setEditingField(data.field);
        setFormData({
          fieldId: data.field.fieldId,
          fieldName: data.field.fieldName,
          fieldType: data.field.fieldType,
          category: data.field.category,
          isRequired: data.field.isRequired,
          isCalculated: data.field.isCalculated,
          isSystemField: data.field.isSystemField || false,
          description: data.field.description || '',
          placeholder: data.field.placeholder || '',
          helpText: data.field.helpText || '',
          options: data.field.options || [],
          validationRules: data.field.validationRules || {}
        });
        setOptionsText((data.field.options || []).join('\n'));
      }
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: catalogApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalogFields'] });
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
      toast.success('Field deleted from catalog');
    },
    onError: (e: Error) => toast.error(`Failed to delete: ${e.message}`),
  });

  const resetForm = () => {
    setFormData({
      fieldId: '',
      fieldName: '',
      fieldType: 'text',
      category: 'risk',
      isRequired: false,
      isCalculated: false,
      isSystemField: false,
      description: '',
      placeholder: '',
      helpText: '',
      options: [],
      validationRules: {}
    });
    setOptionsText('');
  };

  const handleSubmit = () => {
    const data = {
      ...formData,
      options: optionsText.split('\n').filter(line => line.trim()),
    };

    if (editingField) {
      updateMutation.mutate({ catalogId: editingField.catalogId, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (field: CatalogField) => {
    setFormData({
      fieldId: field.fieldId,
      fieldName: field.fieldName,
      fieldType: field.fieldType,
      category: field.category,
      isRequired: field.isRequired,
      isCalculated: field.isCalculated,
      isSystemField: field.isSystemField || false,
      description: field.description || '',
      placeholder: field.placeholder || '',
      helpText: field.helpText || '',
      options: field.options || [],
      validationRules: field.validationRules || {}
    });
    setOptionsText((field.options || []).join('\n'));
    setEditingField(field);
    setIsCreating(true);
  };

  const handleDelete = async (catalogId: string) => {
    const ok = await swal.confirmDelete('this field from the catalog');
    if (!ok) return;
    deleteMutation.mutate(catalogId);
  };

  const filteredFields = catalogFields
    .filter(f => f.category === selectedCategory)
    .filter(f => {
      if (!fieldSearch.trim()) return true;
      const q = fieldSearch.trim().toLowerCase();
      return f.fieldName?.toLowerCase().includes(q) || f.fieldId?.toLowerCase().includes(q);
    });

  const categories = ['risk', 'issue', 'incident', 'control', 'threat', 'remediation', 'evidence', 'document'];
  const fieldTypes: FieldType[] = ['text', 'richtext', 'dropdown', 'searchable-dropdown', 'multiselect', 'person', 'asset', 'date', 'number', 'readonly', 'calculated', 'procedure'];

  if (!isPlatformAdmin(userEmail)) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-danger">
          <i className="ri-error-warning-line"></i>
          Unauthorized: This page is only accessible to Mercell administrators.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      {/* Header */}
      <PageHeader
        icon="ri-list-settings-line"
        title="Field Catalog Builder"
        subtitle="Mercell Admin: Create and manage master field catalog"
        actions={
          <>
            {(isCreating || editingField) && (
              <button
                className="np-btn np-btn-ghost"
                onClick={() => {
                  resetForm();
                  setIsCreating(false);
                  setEditingField(null);
                }}
              >
                <i className="ri-arrow-left-line"></i>
                Back to Catalog
              </button>
            )}
            {!isCreating && !editingField && (
              <button
                className="np-btn np-btn-primary"
                onClick={() => {
                  resetForm();
                  setIsCreating(true);
                }}
              >
                <i className="ri-add-line"></i>
                Create Field
              </button>
            )}
          </>
        }
      />

      {/* Create/Edit Form */}
      {isCreating && (
        <div className="np-card">
          <div className="np-card-head">
            <h2>
              {editingField ? 'Edit Field' : 'Create New Field'}
            </h2>
          </div>
          <div className="np-card-body">
            {(editingField?.isSystemField || formData.isSystemField) && (
              <div className="np-banner np-tone-warning mb-4">
                <i className="ri-lock-line"></i>
                <div>
                  <strong>System Field Notice:</strong> This is a system-managed field with backend logic.
                  {editingField ? 'Field ID, Type, Category, and Calculated status cannot be changed.' : 'Once saved, Field ID, Type, Category, and Calculated status will be locked.'}
                  You can {editingField ? 'update' : 'still edit'} the field name, description, and display-related properties.
                </div>
              </div>
            )}
            <div className="row">
              <div className="col-md-6 np-field">
                <label>
                  Field ID <span className="text-danger">*</span>
                  {(editingField?.isSystemField || (editingField && formData.isSystemField)) && (
                    <span className="np-chip np-tone-warning ms-2">
                      <i className="ri-lock-line"></i> Locked
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  className="np-input"
                  value={formData.fieldId}
                  onChange={(e) => setFormData({ ...formData, fieldId: e.target.value })}
                  placeholder="e.g., custom_field_name"
                  disabled={!!editingField || formData.isSystemField}
                />
                <p className="np-hint">Unique identifier (lowercase, underscores only)</p>
              </div>

              <div className="col-md-6 np-field">
                <label>
                  Field Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="np-input"
                  value={formData.fieldName}
                  onChange={(e) => setFormData({ ...formData, fieldName: e.target.value })}
                  placeholder="e.g., Custom Field Name"
                />
              </div>

              <div className="col-md-4 np-field">
                <label>
                  Field Type <span className="text-danger">*</span>
                  {(editingField && formData.isSystemField) && (
                    <span className="np-chip np-tone-warning ms-2">
                      <i className="ri-lock-line"></i> Locked
                    </span>
                  )}
                </label>
                <select
                  className="np-select"
                  value={formData.fieldType}
                  onChange={(e) => setFormData({ ...formData, fieldType: e.target.value as FieldType })}
                  disabled={!!(editingField && formData.isSystemField)}
                >
                  {fieldTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-4 np-field">
                <label>
                  Category <span className="text-danger">*</span>
                  {(editingField && formData.isSystemField) && (
                    <span className="np-chip np-tone-warning ms-2">
                      <i className="ri-lock-line"></i> Locked
                    </span>
                  )}
                </label>
                <select
                  className="np-select"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  disabled={!!editingField}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-12 np-field">
                <label>Description</label>
                <textarea
                  className="np-textarea"
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this field"
                />
              </div>

              <div className="col-md-6 np-field">
                <label>Placeholder Text</label>
                <input
                  type="text"
                  className="np-input"
                  value={formData.placeholder}
                  onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                  placeholder="e.g., Enter value..."
                />
              </div>

              <div className="col-md-6 np-field">
                <label>Help Text</label>
                <input
                  type="text"
                  className="np-input"
                  value={formData.helpText}
                  onChange={(e) => setFormData({ ...formData, helpText: e.target.value })}
                  placeholder="Additional guidance for users"
                />
              </div>

              {(formData.fieldType === 'dropdown' || formData.fieldType === 'searchable-dropdown' || formData.fieldType === 'multiselect') && (
                <div className="col-md-12 np-field">
                  <label>Dropdown Options</label>
                  <textarea
                    className="np-textarea font-monospace"
                    rows={5}
                    value={optionsText}
                    onChange={(e) => setOptionsText(e.target.value)}
                    placeholder="One option per line&#10;Option 1&#10;Option 2&#10;Option 3"
                  />
                  <p className="np-hint">Enter one option per line</p>
                </div>
              )}

              <div className="col-md-12 np-field">
                <div className="d-flex align-items-center gap-2">
                  <label className="np-switch mb-0">
                    <input
                      type="checkbox"
                      checked={formData.isRequired}
                      onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                    />
                    <span className="np-switch-track"></span>
                    <span className="np-switch-thumb"></span>
                  </label>
                  <label className="mb-0">Required Field</label>
                </div>
                <div className="d-flex align-items-center gap-2 mt-2">
                  <label className="np-switch mb-0">
                    <input
                      type="checkbox"
                      checked={formData.isCalculated}
                      onChange={(e) => setFormData({ ...formData, isCalculated: e.target.checked })}
                      disabled={!!(editingField && formData.isSystemField)}
                    />
                    <span className="np-switch-track"></span>
                    <span className="np-switch-thumb"></span>
                  </label>
                  <label className="mb-0">
                    Calculated Field
                    {(editingField && formData.isSystemField) && (
                      <span className="np-chip np-tone-warning ms-2">
                        <i className="ri-lock-line"></i> Locked
                      </span>
                    )}
                  </label>
                </div>
                <div className="d-flex align-items-center gap-2 mt-2">
                  <label className="np-switch mb-0">
                    <input
                      type="checkbox"
                      checked={formData.isSystemField}
                      onChange={(e) => setFormData({ ...formData, isSystemField: e.target.checked })}
                    />
                    <span className="np-switch-track"></span>
                    <span className="np-switch-thumb"></span>
                  </label>
                  <label className="mb-0">
                    System Field
                  </label>
                </div>
                <p className="np-hint mt-1">
                  System fields have backend logic and restricted editing (locks fieldId, type, category, calculated status)
                </p>
              </div>
            </div>

            <div className="d-flex gap-2">
              <button
                className="np-btn np-btn-primary"
                onClick={handleSubmit}
                disabled={!formData.fieldId || !formData.fieldName || createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className={`ri-${editingField ? 'save' : 'add'}-line`}></i>
                    {editingField ? 'Update' : 'Create'} Field
                  </>
                )}
              </button>
              <button
                className="np-btn np-btn-ghost"
                onClick={() => {
                  resetForm();
                  setIsCreating(false);
                  setEditingField(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Filter */}
      {!isCreating && (
        <div className="np-card">
          <div className="np-card-body">
            <label className="d-block mb-2">Filter by Category:</label>
            <div className="np-pill-toggle-group" role="group">
              {categories.map(cat => (
                <button
                  key={cat}
                  type="button"
                  className={`np-pill-toggle np-tone-primary${selectedCategory === cat ? ' active' : ''}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Fields List */}
      {!isCreating && (
        <div className="np-card">
          <div className="np-card-head">
            <h2>
              {selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Fields ({filteredFields.length})
            </h2>
          </div>
          <div className="np-card-body border-bottom py-3">
            <div className="np-search" style={{ maxWidth: 280 }}>
              <i className="ri-search-line"></i>
              <input
                type="text"
                placeholder="Search fields by name or ID..."
                value={fieldSearch}
                onChange={(e) => setFieldSearch(e.target.value)}
              />
              {fieldSearch && (
                <button type="button" className="np-icon-btn" onClick={() => setFieldSearch('')}>
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </div>
          <div className="np-card-body">
          <NimbusTable thead={<tr>
              <th>Field ID</th>
              <th>Field Name</th>
              <th>Type</th>
              <th>Required</th>
              <th>System</th>
              <th>Actions</th>
            </tr>}>
                {filteredFields.map((field) => (
                  <tr key={field.catalogId}>
                    <td>
                      <code>{field.fieldId}</code>
                    </td>
                    <td>
                      {field.fieldName}
                      {field.isSystemField && (
                        <span className="np-chip np-tone-warning ms-2" title="System-managed field with restricted editing">
                          <i className="ri-lock-line"></i>
                          System
                        </span>
                      )}
                      {field.isCalculated && (
                        <span className="np-chip np-tone-info ms-2" title="Calculated field">
                          <i className="ri-calculator-line"></i>
                          Calculated
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="np-chip np-tone-primary">{field.fieldType}</span>
                    </td>
                    <td>
                      {field.isRequired ? (
                        <i className="ri-checkbox-circle-line text-success"></i>
                      ) : (
                        <i className="ri-close-circle-line" style={{ color: 'var(--bs-secondary-color)' }}></i>
                      )}
                    </td>
                    <td>
                      {field.isSystemField ? (
                        <i className="ri-lock-fill text-warning" title="System field"></i>
                      ) : (
                        <i className="ri-user-line" style={{ color: 'var(--bs-secondary-color)' }} title="Custom field"></i>
                      )}
                    </td>
                    <td>
                      <button
                        className="np-icon-btn me-2"
                        onClick={() => handleEdit(field)}
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button
                        className="np-icon-btn np-icon-btn-danger"
                        onClick={() => handleDelete(field.catalogId)}
                        disabled={field.isSystemField}
                        title={field.isSystemField ? 'Cannot delete system fields' : 'Delete field'}
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredFields.length === 0 && (
                  <tr>
                    <td colSpan={6} className="np-sub text-center py-4">
                      <i className="ri-list-settings-line me-1"></i>{fieldSearch ? `No fields match "${fieldSearch}"` : 'No fields found for this category'}
                    </td>
                  </tr>
                )}
          </NimbusTable>
          </div>
        </div>
      )}
    </div>
  );
};
